import catalogue from '../data/clubs.json';

export interface ClubCandidate {
  id: string;
  name: string;
  logoUrl: string;
}

export interface ReadyClub extends ClubCandidate {
  image: HTMLImageElement;
}

export const CLUB_PAGE_SIZE = 16;

export interface ClubPage {
  clubs: ClubCandidate[];
  endOfPass: boolean;
}

/** Section-owned metadata only. Complete each shuffled pass before reshuffling. */
export function createClubPager(records: readonly ClubCandidate[] = catalogue) {
  let pass: ClubCandidate[] = [];
  let cursor = 0;
  let previousTail = new Set<string>();
  return async (): Promise<ClubPage> => {
    if (cursor >= pass.length) {
      const shuffled = shuffleClubs([...records]);
      // Defer the previous pass's last batch to avoid immediate boundary repeats.
      pass = [...shuffled.filter(club => !previousTail.has(club.id)),
        ...shuffled.filter(club => previousTail.has(club.id))];
      cursor = 0;
    }
    const clubs = pass.slice(cursor, cursor + CLUB_PAGE_SIZE);
    cursor += clubs.length;
    const endOfPass = cursor >= pass.length;
    if (endOfPass) previousTail = new Set(pass.slice(-CLUB_PAGE_SIZE).map(club => club.id));
    return { clubs, endOfPass };
  };
}

/** Keep and later mount this exact decoded element, avoiding a second image load at the flip. */
export function preloadClub(club: ClubCandidate, signal: AbortSignal): Promise<ReadyClub> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    let settled = false;
    const cleanup = () => {
      window.clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
      image.onload = null;
      image.onerror = null;
    };
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      image.removeAttribute('src');
      reject(error);
    };
    const abort = () => fail(new DOMException('Logo loading paused', 'AbortError'));
    const timeout = window.setTimeout(() => fail(new Error('Logo loading timed out')), 10000);
    signal.addEventListener('abort', abort, { once: true });
    image.decoding = 'async';
    image.alt = '';
    image.onload = async () => {
      try {
        if (typeof image.decode !== 'function') throw new Error('Image decoding unavailable');
        await image.decode();
        if (settled) return;
        if (!image.naturalWidth || !image.naturalHeight) throw new Error('Empty logo');
        settled = true;
        cleanup();
        resolve({ ...club, image });
      } catch {
        fail(new Error('Logo decoding failed'));
      }
    };
    image.onerror = () => fail(new Error('Logo loading failed'));
    if (signal.aborted) abort();
    else image.src = club.logoUrl;
  });
}

export function shuffleClubs<T>(items: T[]): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index--) {
    const next = Math.floor(Math.random() * (index + 1));
    [result[index], result[next]] = [result[next], result[index]];
  }
  return result;
}
